import { Add, Code, Save, Splitscreen, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Chip, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { FormBuilderState } from "../../types/formBuilder.types";
import TranslatableInput from "component/global/translation/TranslatableInput";
import { Ref, useState } from "react";
import { useTranslation } from "react-i18next";

interface BuilderToolbarProps {
    state: FormBuilderState;
    displayTitle?: string;
    saving?: boolean;
    previewVisible: boolean;
    onTitleChange: (title: string, displayTitle?: string) => void;
    onTitleDraftChange?: (draft: string) => void;
    onTitleDirtyChange?: (dirty: boolean) => void;
    registerTitleFlush?: (flush: () => Promise<boolean>) => () => void;
    titleInputRef?: Ref<HTMLInputElement>;
    titleError?: string | null;
    onViewChange: (view: "nocode" | "developer") => void;
    onAddStep: () => void;
    onTogglePreview: () => void;
    onSave?: () => void;
}

const BuilderToolbar = ({ state, displayTitle, saving = false, previewVisible, onTitleChange, onTitleDraftChange, onTitleDirtyChange, registerTitleFlush, titleInputRef, titleError, onViewChange, onAddStep, onTogglePreview, onSave }: BuilderToolbarProps) => {
    const { t } = useTranslation();
    const [advanced, setAdvanced] = useState(false);

    return (
        <Box className="service-builder__toolbar">
            <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0} className="service-builder__identity">
                <Box className="service-builder__title-input">
                    <TranslatableInput
                        label="Service form title"
                        value={state.formTitle}
                        fallbackText={displayTitle}
                        suggestedKey={`sp.forms.${state.translationId}.title`}
                        defaultPublic={true}
                        onChange={onTitleChange}
                        onDraftChange={onTitleDraftChange}
                        onDirtyChange={onTitleDirtyChange}
                        registerFlush={registerTitleFlush}
                        inputRef={titleInputRef}
                        invalid={Boolean(titleError)}
                        helperText={titleError || undefined}
                        showTranslationKey={advanced}
                    />
                </Box>
                {advanced && <Stack spacing={0.25} minWidth={0} className="service-builder__identity-meta"><Typography variant="caption" noWrap>{t("Internal name")}: {state.formName}</Typography><Typography variant="caption" noWrap>{t("Slug")}: {state.formSlug}</Typography></Stack>}
                <Chip size="small" label={t("{{count}} field", { count: state.fields.length, defaultValue_one: "{{count}} field", defaultValue_other: "{{count}} fields" })} />
                <Chip size="small" label={state.targetTable || t("No target table")} variant="outlined" />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={() => setAdvanced((value) => !value)}>{t("Advanced")}</Button>
                <Tooltip title={t("Add wizard step")}>
                    <Button size="small" startIcon={<Add />} onClick={onAddStep} variant="outlined">{t("Step")}</Button>
                </Tooltip>
                <ButtonGroup size="small" variant="outlined">
                    <Button startIcon={<Splitscreen />} variant={state.activeView === "nocode" ? "contained" : "outlined"} onClick={() => onViewChange("nocode")}>{t("No-Code")}</Button>
                    <Button startIcon={<Code />} variant={state.activeView === "developer" ? "contained" : "outlined"} onClick={() => onViewChange("developer")}>{t("Developer")}</Button>
                </ButtonGroup>
                <Tooltip title={previewVisible ? t("Hide preview") : t("Show preview")}>
                    <Button size="small" startIcon={previewVisible ? <VisibilityOff /> : <Visibility />} variant={previewVisible ? "contained" : "outlined"} onClick={onTogglePreview}>{t("service_builder.preview", { defaultValue: "Preview" })}</Button>
                </Tooltip>
                {onSave && (
                    <Button size="small" variant="contained" startIcon={saving ? <CircularProgress color="inherit" size={14} /> : <Save />} onClick={onSave} disabled={saving}>{t("Save")}</Button>
                )}
            </Stack>
        </Box>
    );
};

export default BuilderToolbar;
